import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { resolveImageUrl } from "../../context/ContentContext";

export default function ImagePicker({ value, onChange, aspect = "4/3", compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = resolveImageUrl(value);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB).");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded.");
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div
        className="relative rounded-2xl border border-dashed border-[#E5E0D8] bg-[#F5F2EB] overflow-hidden"
        style={{ aspectRatio: aspect }}
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#6B6B6B] text-xs gap-2">
            <ImageIcon className="w-8 h-8 opacity-50" />
            <span>No image</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#B8860B]" />
          </div>
        )}
        {preview && !uploading && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
            data-testid="image-picker-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className={`flex gap-2 ${compact ? "" : ""}`}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex-1 px-3 py-2 rounded-xl text-xs uppercase tracking-wider font-semibold bg-[#1A1A1A] text-white hover:bg-[#2C2C2C] flex items-center justify-center gap-2"
          data-testid="image-picker-upload"
        >
          <Upload className="w-3.5 h-3.5" /> Upload
        </button>
      </div>

      <input
        type="url"
        placeholder="…or paste an image URL"
        className="input-x text-xs"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid="image-picker-url"
      />
    </div>
  );
}
